import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MdCalendarToday,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdFileDownload,
  MdOutlinePictureAsPdf,
  MdRefresh,
  MdTune,
} from "react-icons/md";
import {
  endOfMonth,
  format,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { toast } from "sonner";
import { ProjectApi } from "api/projects/service";
import { BASE_URL } from "../../../utils/constant";
import GlobalPDFReport, {
  buildPlaceholderGlobalReportData,
} from "./GlobalPDFReport";
import RequireAccess from "components/RequireAccess";

const REPORT_SECTIONS = [
  {
    id: "design",
    label: "Design",
    description: "Design KPIs with pending and resolved pin details",
  },
  {
    id: "contractor_overview",
    label: "Contractor",
    description: "Workorders, attendance, and contractor payment data",
  },
  {
    id: "petty_cash",
    label: "Petty Cash",
    description: "Allocation, balance, and approval summaries",
  },
  {
    id: "vendors",
    label: "Vendors",
    description: "Vendor payment summary and advance payment log",
  },
  {
    id: "procurement",
    label: "Procurement",
    description: "MRS, RFQ, PO status and recent purchase orders",
  },
  {
    id: "finance",
    label: "Finance",
    description: "Organization financial summary and analytics",
  },
];

const DEFAULT_SECTION_IDS = REPORT_SECTIONS.map((section) => section.id);

const normalizeSites = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getDefaultDateRange = () => ({
  start: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
  end: format(new Date(), "yyyy-MM-dd"),
});

const getProjectName = (site) =>
  site?.site_name?.trim() || site?.name?.trim() || "Untitled Project";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "₹0";
  return `₹${amount.toLocaleString("en-IN")}`;
};

const formatPreviewDate = (value) => {
  if (!value) return "-";

  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch (error) {
    return String(value);
  }
};

const buildPreviewCards = (summary = {}) => [
  { label: "Total Expenses", value: summary.total_expenses },
  { label: "Dues", value: summary.total_dues },
  { label: "Received", value: summary.total_received },
  { label: "Balance", value: summary.balance },
];

const getStoredOrganization = () => {
  try {
    return JSON.parse(localStorage.getItem("organization"));
  } catch (error) {
    return null;
  }
};

const ReportPreview = React.memo(function ReportPreview({
  loading,
  documentData,
  logoSrc,
  isPlaceholder,
}) {
  return (
    <div className="mt-2 h-[100%] overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
      <div className="relative h-full w-full bg-white">
        {loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/35 backdrop-blur-sm">
            <div className="rounded-3xl border border-white/15 bg-slate-950/75 px-6 py-5 text-center text-white">
              <div className="mx-auto mb-3 h-6 w-6 rounded-full border-2 border-white/25 border-t-white animate-spin" />
              <p className="text-sm font-semibold">Building your live report</p>
              <p className="mt-1 text-xs text-slate-300">
                The preview will update in place as soon as the PDF is ready.
              </p>
            </div>
          </div>
        ) : null}

        <PDFViewer className="h-full w-full border-none">
          <GlobalPDFReport
            data={documentData}
            logoSrc={logoSrc}
            isPlaceholder={isPlaceholder}
          />
        </PDFViewer>
      </div>
    </div>
  );
});

const GlobalReport = ({ organizationId, currentMember, permissionsLoaded }) => {
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().start);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().end);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedSections, setSelectedSections] = useState(DEFAULT_SECTION_IDS);
  const [sites, setSites] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState("");
  const [generateError, setGenerateError] = useState("");
  const placeholderGeneratedAtRef = useRef(new Date().toISOString());

  const storedOrganization = getStoredOrganization();
  const resolvedOrganizationId = organizationId || storedOrganization?.id;
  const organizationLogo = storedOrganization?.logo_image_file || null;
  const organizationName =
    storedOrganization?.name ||
    storedOrganization?.company_name ||
    storedOrganization?.organization_name ||
    "Organization";

  const datePresets = [
    {
      label: "Last 7 Days",
      getValue: () => ({
        start: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        end: format(new Date(), "yyyy-MM-dd"),
      }),
    },
    {
      label: "Last 30 Days",
      getValue: () => ({
        start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
        end: format(new Date(), "yyyy-MM-dd"),
      }),
    },
    {
      label: "This Month",
      getValue: () => ({
        start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
      }),
    },
    {
      label: "Last Month",
      getValue: () => ({
        start: format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
        end: format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
      }),
    },
  ];

  useEffect(() => {
    let isMounted = true;

    const loadSites = async () => {
      if (!resolvedOrganizationId) {
        setSites([]);
        setSelectedProjectIds([]);
        return;
      }

      setSitesLoading(true);
      setSitesError("");

      try {
        const response = await ProjectApi.listSitesByOrganization(
          resolvedOrganizationId
        );
        const siteList = normalizeSites(response);

        if (!isMounted) return;

        setSites(siteList);
        setSelectedProjectIds((current) => {
          const validIds = current.filter((projectId) =>
            siteList.some((site) => String(site.id) === String(projectId))
          );

          if (validIds.length > 0) return validIds;

          return siteList.map((site) => site.id);
        });
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching sites for global report:", error);
        setSites([]);
        setSelectedProjectIds([]);
        setSitesError("Unable to load projects for this organization.");
      } finally {
        if (isMounted) {
          setSitesLoading(false);
        }
      }
    };

    loadSites();

    return () => {
      isMounted = false;
    };
  }, [resolvedOrganizationId]);

  const isSelected = (values, id) =>
    values.some((item) => String(item) === String(id));

  const toggleSelection = (id, setValues) => {
    setValues((current) =>
      isSelected(current, id)
        ? current.filter((item) => String(item) !== String(id))
        : [...current, id]
    );
  };

  const resetFilters = () => {
    const defaultDateRange = getDefaultDateRange();

    setReportData(null);
    setIsDownloading(false);
    setGenerateError("");
    setStartDate(defaultDateRange.start);
    setEndDate(defaultDateRange.end);
    setSelectedSections(DEFAULT_SECTION_IDS);
    setSelectedProjectIds(sites.map((site) => site.id));
  };

  const handlePDFDownload = (event) => {
    if (isDownloading) {
      event.preventDefault();
      return false;
    }

    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 3000);
  };

  const isDateRangeInvalid =
    Boolean(startDate) &&
    Boolean(endDate) &&
    new Date(startDate).getTime() > new Date(endDate).getTime();

  const selectedProjectNames = useMemo(
    () =>
      sites
        .filter((site) => isSelected(selectedProjectIds, site.id))
        .map(getProjectName),
    [sites, selectedProjectIds]
  );

  const selectedSectionLabels = useMemo(
    () =>
      REPORT_SECTIONS.filter((section) =>
        isSelected(selectedSections, section.id)
      ).map((section) => section.label),
    [selectedSections]
  );

  const handleGenerateReport = async () => {
    if (!resolvedOrganizationId) {
      toast.error("Organization ID not found.");
      return;
    }

    if (isDateRangeInvalid) {
      const message = "Start date must be on or before the end date.";
      setGenerateError(message);
      toast.error(message);
      return;
    }

    if (selectedSections.length === 0) {
      const message = "Please select at least one section.";
      setGenerateError(message);
      toast.error(message);
      return;
    }

    if (selectedProjectIds.length === 0) {
      const message = "Please select at least one project.";
      setGenerateError(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    setGenerateError("");

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/org/organization/${resolvedOrganizationId}/global-report/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            start_date: startDate,
            end_date: endDate,
            sections: selectedSections,
            project_ids: selectedProjectIds,
          }),
        }
      );

      let result = {};

      try {
        result = await response.json();
      } catch (error) {
        result = {};
      }

      const payload = result?.data ?? result;
      const isSuccess =
        typeof result?.status === "boolean"
          ? result.status
          : Boolean(payload?.meta || payload?.summary || payload?.projects);

      if (!response.ok || !isSuccess) {
        throw new Error(result?.message || "Failed to generate report");
      }

      setReportData(payload);
    } catch (error) {
      const message =
        error?.message || "An error occurred while generating the report.";

      console.error("Error generating global report:", error);
      setGenerateError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const placeholderDocumentData = useMemo(
    () =>
      buildPlaceholderGlobalReportData({
        companyName: organizationName,
        startDate,
        endDate,
        selectedSections: selectedSectionLabels,
        selectedProjects: selectedProjectNames,
        generatedAt: placeholderGeneratedAtRef.current,
      }),
    [
      endDate,
      organizationName,
      selectedProjectNames,
      selectedSectionLabels,
      startDate,
    ]
  );

  const previewDocumentData = reportData || placeholderDocumentData;
  const previewSummary = reportData?.summary || reportData?.org_finance?.summary || {};
  const previewCards = buildPreviewCards(previewSummary);
  const hasGeneratedReport = Boolean(reportData);
  const previewMeta = previewDocumentData?.meta || {};
  const generatedSectionIds = previewMeta?.selected_sections || [];
  const generatedProjectNames = previewMeta?.selected_projects || [];
  const previewProjectCount = hasGeneratedReport
    ? generatedProjectNames.length || selectedProjectIds.length
    : selectedProjectIds.length;
  const previewSectionCount = hasGeneratedReport
    ? generatedSectionIds.length || selectedSections.length
    : selectedSections.length;
  const fileName = `${String(
    previewMeta?.report_name || "Global_Project_Report"
  ).replace(/\s+/g, "_")}_${format(new Date(), "yyyyMMdd")}.pdf`;
  const generateHint = (() => {
    if (generateError) return generateError;
    if (!resolvedOrganizationId) return "Organization context is required.";
    if (isDateRangeInvalid)
      return "Start date must be on or before the end date.";
    if (selectedSections.length === 0) return "Select at least one section.";
    if (selectedProjectIds.length === 0) return "Select at least one project.";
    if (sitesError) return sitesError;
    return "Generate once to replace the sample with your live multi-page PDF.";
  })();

  const isGenerateDisabled =
    loading ||
    sitesLoading ||
    !resolvedOrganizationId ||
    isDateRangeInvalid ||
    selectedSections.length === 0 ||
    selectedProjectIds.length === 0;
  if (
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false &&
    permissionsLoaded
  ) {
    return (
      <RequireAccess
        module="Global Report"
        currentMember={currentMember}
        permission="global_report"
      />
    );
  }
  return (
    <div className="flex-1 h-[91vh] overflow-hidden" style={{
      scrollbarWidth: "none"
    }}>
      <div className="px-10 py-8">
        <div className="mb-8 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Global Project Report
              </h1>
              <p className="text-sm text-muted-foreground">
                Generate a comprehensive report across all your projects
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2 backdrop-blur">
              {/* <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Current Setup
              </p> */}
              <p className="text-sm font-medium text-slate-900">
                {selectedSections.length} sections • {selectedProjectIds.length}{" "}
                projects
              </p>
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={isGenerateDisabled}
              className={`inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition ${isGenerateDisabled
                ? "cursor-not-allowed bg-[#3B0069]/50 text-white/50"
                : "bg-[#3B0069] text-white hover:-translate-y-0.5 hover:bg-[#3B0069]/80"
                }`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-[#3B0069]/25 border-t-[#3B0069] animate-spin" />
                  Generating report...
                </span>
              ) : (
                "Generate PDF Preview"
              )}
            </button>
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700  transition hover:border-slate-300 hover:bg-slate-50"
            >
              <MdRefresh size={18} />
              Reset
            </button>
          </div>
        </div>

        <div className="grid items-start gap-6 xl:grid-cols-[480px,minmax(0,1fr)]">
          <div className="space-y-5 ">
            <section className="rounded-lg border h-[76vh] overflow-y-scroll border-white/70 bg-white/90 p-4 backdrop-blur sm:p-3">
              {/* <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#3B0069]">
                    Filters
                  </p>
                  <h2 className="mt-1 text-xl font-semibold text-slate-950">
                    Report Controls
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Keep the preview open while you refine the report inputs.
                  </p>
                </div>
                <div className="rounded-2xl bg-[#3B0069]/8 p-3 text-[#3B0069]">
                  <MdTune size={22} />
                </div>
              </div> */}

              <div className="space-y-6">
                <div>
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <MdCalendarToday className="text-[#3B0069]" />
                    Date Range
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {datePresets.map((preset) => {
                      const { start, end } = preset.getValue();
                      const isActive = startDate === start && endDate === end;

                      return (
                        <button
                          key={preset.label}
                          onClick={() => {
                            setStartDate(start);
                            setEndDate(end);
                            setGenerateError("");
                          }}
                          className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition ${isActive
                            ? "border-[#3B0069] bg-[#3B0069] text-white shadow-lg shadow-[#3B0069]/20"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:border-[#3B0069]/25 hover:bg-[#F7F2FF] hover:text-[#3B0069]"
                            }`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(event) => {
                          setStartDate(event.target.value);
                          setGenerateError("");
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-[#3B0069]/10"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(event) => {
                          setEndDate(event.target.value);
                          setGenerateError("");
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-[#3B0069]/10"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        Sections
                      </h3>
                      <p className="text-xs text-slate-500">
                        Pick the modules that should appear in the PDF.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <button
                        onClick={() => setSelectedSections(DEFAULT_SECTION_IDS)}
                        className="text-[#3B0069] transition hover:text-[#2A004D]"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedSections([])}
                        className="text-slate-500 transition hover:text-slate-700"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {REPORT_SECTIONS.map((section) => {
                      const sectionIsSelected = isSelected(
                        selectedSections,
                        section.id
                      );

                      return (
                        <button
                          key={section.id}
                          onClick={() =>
                            toggleSelection(section.id, setSelectedSections)
                          }
                          className={`rounded-lg border px-3 py-2 text-left transition ${sectionIsSelected
                            ? "border-[#3B0069] bg-[#F7F2FF]"
                            : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"
                            }`}
                        >
                          <div className="flex items-start gap-3">
                            {sectionIsSelected ? (
                              <MdCheckBox
                                className="mt-0.5 shrink-0 text-[#3B0069]"
                                size={18}
                              />
                            ) : (
                              <MdCheckBoxOutlineBlank
                                className="mt-0.5 shrink-0 text-slate-300"
                                size={18}
                              />
                            )}
                            <div className="min-w-0">
                              <p
                                className={`text-sm font-semibold ${sectionIsSelected
                                  ? "text-[#3B0069]"
                                  : "text-slate-800"
                                  }`}
                              >
                                {section.label}
                              </p>
                              {/* <p className="mt-1 text-xs leading-5 text-slate-500">
                                {section.description}
                              </p> */}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-6">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        Projects
                      </h3>
                      <p className="text-xs text-slate-500">
                        Compact project selection for the final report.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <button
                        onClick={() =>
                          setSelectedProjectIds(sites.map((site) => site.id))
                        }
                        className="text-[#3B0069] transition hover:text-[#2A004D]"
                        disabled={sites.length === 0}
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedProjectIds([])}
                        className="text-slate-500 transition hover:text-slate-700"
                        disabled={sites.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {sitesLoading ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      Loading projects...
                    </div>
                  ) : sitesError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-600">
                      {sitesError}
                    </div>
                  ) : sites.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No projects available for this organization.
                    </div>
                  ) : (
                    <div className="space-y-2 pr-1">
                      {sites.map((site) => {
                        const projectIsSelected = isSelected(
                          selectedProjectIds,
                          site.id
                        );

                        return (
                          <button
                            key={site.id}
                            onClick={() =>
                              toggleSelection(site.id, setSelectedProjectIds)
                            }
                            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${projectIsSelected
                              ? "border-[#3B0069] bg-[#F7F2FF]"
                              : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                              }`}
                          >
                            {projectIsSelected ? (
                              <MdCheckBox
                                className="shrink-0 text-[#3B0069]"
                                size={18}
                              />
                            ) : (
                              <MdCheckBoxOutlineBlank
                                className="shrink-0 text-slate-300"
                                size={18}
                              />
                            )}
                            <span
                              className={`min-w-0 flex-1 truncate text-sm font-medium ${projectIsSelected
                                ? "text-[#3B0069]"
                                : "text-slate-700"
                                }`}
                            >
                              {getProjectName(site)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* <section className="rounded-lg border border-[#3B0069]/8 bg-slate-950 px-5 py-5 text-white  sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-200/70">
                    Generate
                  </p>
                  <h2 className="mt-1 text-xl font-semibold">
                    Refresh the preview
                  </h2>
                </div>
                <div className="rounded-2xl bg-white/10 p-3 text-violet-100">
                  <MdOutlinePictureAsPdf size={22} />
                </div>
              </div>

              <p className="mt-3 text-sm leading-6 text-slate-300">
                {generateHint}
              </p>

              <button
                onClick={handleGenerateReport}
                disabled={isGenerateDisabled}
                className={`mt-5 inline-flex w-full items-center justify-center rounded-2xl px-4 py-3.5 text-sm font-semibold transition ${isGenerateDisabled
                    ? "cursor-not-allowed bg-white/10 text-white/50"
                    : "bg-white text-[#3B0069] hover:-translate-y-0.5 hover:bg-violet-50"
                  }`}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-[#3B0069]/25 border-t-[#3B0069] animate-spin" />
                    Generating report...
                  </span>
                ) : (
                  "Generate PDF Preview"
                )}
              </button>
            </section> */}
          </div>

          <div className="h-full">
            <section className="rounded-lg border h-full border-white/70 bg-white/90 px-4 py-3 backdrop-blur sm:p-5">
              <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-[#3B0069]/10 p-2 text-[#3B0069]">
                    <MdOutlinePictureAsPdf size={22} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">
                      PDF Preview
                    </h2>
                  </div>
                </div>

                {hasGeneratedReport ? (
                  <PDFDownloadLink
                    document={
                      <GlobalPDFReport
                        data={previewDocumentData}
                        logoSrc={organizationLogo}
                      />
                    }
                    fileName={fileName}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                      isDownloading
                        ? "pointer-events-none bg-slate-200 text-slate-500"
                        : "bg-[#3B0069] text-white hover:bg-[#3B0069]/80"
                    }`}
                    onClick={handlePDFDownload}
                  >
                    {({ loading: pdfLoading }) => (
                      <>
                        <MdFileDownload size={18} />
                        {pdfLoading
                          ? "Preparing PDF..."
                          : isDownloading
                          ? "Download Started..."
                          : "Download PDF"}
                      </>
                    )}
                  </PDFDownloadLink>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-400"
                  >
                    <MdFileDownload size={18} />
                    Generate to download
                  </button>
                )}
              </div>

              {/* <div className="rounded-[26px] border border-slate-200/80 bg-[linear-gradient(135deg,_#111827_0%,_#1F1147_45%,_#3B0069_100%)] p-5 text-white">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-2xl bg-white/10 p-3 text-violet-100">
                        <MdOutlinePictureAsPdf size={22} />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold">PDF Preview</h2>
                        <p className="text-sm text-violet-100/80">
                          {hasGeneratedReport
                            ? "Live preview from the latest generated report."
                            : "Professional sample state until you generate live data."}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-violet-50">
                        {hasGeneratedReport ? "Live data" : "Sample layout"}
                      </span>
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-violet-50">
                        {previewSectionCount} sections
                      </span>
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-violet-50">
                        {previewProjectCount} projects
                      </span>
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-violet-50">
                        {formatPreviewDate(previewMeta?.period?.start || startDate)}{" "}
                        to{" "}
                        {formatPreviewDate(previewMeta?.period?.end || endDate)}
                      </span>
                    </div>
                  </div>

                  {hasGeneratedReport ? (
                    <PDFDownloadLink
                      document={
                        <GlobalPDFReport
                          data={previewDocumentData}
                          logoSrc={organizationLogo}
                        />
                      }
                      fileName={fileName}
                    >
                      {({ loading: pdfLoading }) => (
                        <button
                          disabled={pdfLoading}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-[#3B0069] transition hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          <MdFileDownload size={18} />
                          {pdfLoading ? "Preparing PDF..." : "Download PDF"}
                        </button>
                      )}
                    </PDFDownloadLink>
                  ) : (
                    <button
                      disabled
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-semibold text-white/60"
                    >
                      <MdFileDownload size={18} />
                      Generate to download
                    </button>
                  )}
                </div>

                {hasGeneratedReport ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {previewCards.map((card) => (
                      <div
                        key={card.label}
                        className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur"
                      >
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-violet-100/70">
                          {card.label}
                        </p>
                        <p className="mt-2 text-lg font-semibold">
                          {formatCurrency(card.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div> */}
              <ReportPreview
                loading={loading}
                documentData={previewDocumentData}
                logoSrc={organizationLogo}
                isPlaceholder={!hasGeneratedReport}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalReport;
